<template>
  <component :is="tag" :id="id" class="bim-row" :class="[alignClass, justifyClass, {'no-gutters': noGutters}]">
    <!-- @slot Безымянный слот. Используется для добавление сюда содержимого -->
    <slot></slot>
  </component>
</template>

<script>
export default {
  name: 'bimRow',
  props: {
    /**
     * Какой тег будет у контейнера
     */
    tag: {
      type: String,
      default: 'div'
    },
    /**
     * Добавляет контейнеру id
     */
    id: String,
    /**
     * Применяет свойство css align-items для всех экранов от 320px
     * @values start, center, end, baseline, stretch
     */
    align: {
      type: String,
      validator (value) {
        return ['start', 'center', 'end', 'baseline', 'stretch'].indexOf(value) !== -1
      }
    },
    /**
     * align-items для экранов от 576px
     * @values start, center, end, baseline, stretch
     */
    alignSm: {
      type: String,
      validator (value) {
        return ['start', 'center', 'end', 'baseline', 'stretch'].indexOf(value) !== -1
      }
    },
    /**
     * align-items для экранов от 768px
     * @values start, center, end, baseline, stretch
     */
    alignMd: {
      type: String,
      validator (value) {
        return ['start', 'center', 'end', 'baseline', 'stretch'].indexOf(value) !== -1
      }
    },
    /**
     * align-items для экранов от 960px
     * @values start, center, end, baseline, stretch
     */
    alignLg: {
      type: String,
      validator (value) {
        return ['start', 'center', 'end', 'baseline', 'stretch'].indexOf(value) !== -1
      }
    },
    /**
     * align-items для экранов от 1280px
     * @values start, center, end, baseline, stretch
     */
    alignXl: {
      type: String,
      validator (value) {
        return ['start', 'center', 'end', 'baseline', 'stretch'].indexOf(value) !== -1
      }
    },
    /**
     * align-items для экранов от 1480px
     * @values start, center, end, baseline, stretch
     */
    alignXxl: {
      type: String,
      validator (value) {
        return ['start', 'center', 'end', 'baseline', 'stretch'].indexOf(value) !== -1
      }
    },
    /**
     * Применяет свойство css justify-content для всех экранов от 320px
     * @values start, center, end, space-between, space-around
     */
    justify: {
      type: String,
      validator (value) {
        return ['start', 'center', 'end', 'space-between', 'space-around'].indexOf(value) !== -1
      }
    },
    /**
     * justify-content для экранов от 576px
     * @values start, center, end, space-between, space-around
     */
    justifySm: {
      type: String,
      validator (value) {
        return ['start', 'center', 'end', 'space-between', 'space-around'].indexOf(value) !== -1
      }
    },
    /**
     * justify-content для экранов от 768px
     * @values start, center, end, space-between, space-around
     */
    justifyMd: {
      type: String,
      validator (value) {
        return ['start', 'center', 'end', 'space-between', 'space-around'].indexOf(value) !== -1
      }
    },
    /**
     * justify-content для экранов от 960px
     * @values start, center, end, space-between, space-around
     */
    justifyLg: {
      type: String,
      validator (value) {
        return ['start', 'center', 'end', 'space-between', 'space-around'].indexOf(value) !== -1
      }
    },
    /**
     * justify-content для экранов от 1280px
     * @values start, center, end, space-between, space-around
     */
    justifyXl: {
      type: String,
      validator (value) {
        return ['start', 'center', 'end', 'space-between', 'space-around'].indexOf(value) !== -1
      }
    },
    /**
     * justify-content для экранов от 1480px
     * @values start, center, end, space-between, space-around
     */
    justifyXxl: {
      type: String,
      validator (value) {
        return ['start', 'center', 'end', 'space-between', 'space-around'].indexOf(value) !== -1
      }
    },
    /**
     * Убирает отступы слева и справа
     */
    noGutters: Boolean
  },
  computed: {
    /**
     * Вычисляет какие классы применить в зависимости от заполненных пропов
     */
    justifyClass () {
      let justify, justifySm, justifyMd, justifyLg, justifyXl, justifyXxl

      if (this.justify) justify = this.checkJustify('justify')
      if (this.justifySm) justifySm = this.checkJustify('justifySm')
      if (this.justifyMd) justifyMd = this.checkJustify('justifyMd')
      if (this.justifyLg) justifyLg = this.checkJustify('justifyLg')
      if (this.justifyXl) justifyXl = this.checkJustify('justifyXl')
      if (this.justifyXxl) justifyXxl = this.checkJustify('justifyXxl')

      return [justify, justifySm, justifyMd, justifyLg, justifyXl, justifyXxl]
    },
    /**
     * Вычисляет какие классы применить в зависимости от заполненных пропов
     */
    alignClass () {
      let align, alignSm, alignMd, alignLg, alignXl, alignXxl

      if (this.align) align = this.checkAlign('align')
      if (this.alignSm) alignSm = this.checkAlign('alignSm')
      if (this.alignMd) alignMd = this.checkAlign('alignMd')
      if (this.alignLg) alignLg = this.checkAlign('alignLg')
      if (this.alignXl) alignXl = this.checkAlign('alignXl')
      if (this.alignXxl) alignXxl = this.checkAlign('alignXxl')

      return [align, alignSm, alignMd, alignLg, alignXl, alignXxl]
    },
  },
  methods: {
    /**
     * Универсальная функция для вычисления какой класс нужно вернуть в зависимости от переданного параметра
     * @param {string} val - название переменной-пропа для align.
     */
    checkAlign (val) {
      const cutVal = val.split('align').join('').toLowerCase()
      const prefix = cutVal ? `-${cutVal}-` : '-'
      let result

      switch (this[val]) {
        case 'start':
          result = `align${prefix}start`
          break
        case 'center':
          result = `align${prefix}center`
          break
        case 'end':
          result = `align${prefix}end`
          break
        case 'baseline':
          result = `align${prefix}baseline`
          break
        case 'stretch':
          result = `align${prefix}stretch`
          break
      }
      return result
    },
    /**
     * Универсальная функция для вычисления какой класс нужно вернуть в зависимости от переданного параметра
     * @param {string} val - название переменной-пропа для justify.
     */
    checkJustify (val) {
      const cutVal = val.split('justify').join('').toLowerCase()
      const prefix = cutVal ? `-${cutVal}-` : '-'
      let result

      switch (this[val]) {
        case 'start':
          result = `justify${prefix}start`
          break
        case 'center':
          result = `justify${prefix}center`
          break
        case 'end':
          result = `justify${prefix}end`
          break
        case 'space-between':
          result = `justify${prefix}space-between`
          break
        case 'space-around':
          result = `justify${prefix}space-around`
          break
      }
      return result
    }
  }
}
</script>

<style lang="scss" scoped>
  // Основный стили для строки
  .bim-row {
    box-sizing: border-box;
    display: flex;
    flex-wrap: wrap;
    flex: 1 1 auto;
    @media screen and (min-width: $breakpoint-xs) {
      margin-right: -$col-gap-xs;
      margin-left: -$col-gap-xs;
    }
    @media screen and (min-width: $breakpoint-sm) {
      margin-right: -$col-gap-sm;
      margin-left: -$col-gap-sm;
    }
    @media screen and (min-width: $breakpoint-md) {
      margin-right: -$col-gap-md;
      margin-left: -$col-gap-md;
    }
    @media screen and (min-width: $breakpoint-lg) {
      margin-right: -$col-gap-lg;
      margin-left: -$col-gap-lg;
    }
    @media screen and (min-width: $breakpoint-xl) {
      margin-right: -$col-gap-xl;
      margin-left: -$col-gap-xl;
    }
    @media screen and (min-width: $breakpoint-xxl) {
      margin-right: -$col-gap-xxl;
      margin-left: -$col-gap-xxl;
    }
  }

  $align:
    'start' flex-start,
    'center' center,
    'end' flex-end,
    'baseline' baseline,
    'stretch' stretch;

  $justify:
    'start' flex-start,
    'center' center,
    'end' flex-end,
    'space-between' space-between,
    'space-around' space-around;

  // Для каждого брейкпоинта создается отдельный класс

  @media screen and (min-width: $breakpoint-xs) {
    @each $i in $align {
      // align-*
      .align-#{nth($i, 1)} {
        align-items: nth($i, 2);
      }
      // align-content-*
      .align-content-#{nth($i, 1)} {
        align-content: nth($i, 2);
      }
      // align-self-*
      .align-self-#{nth($i, 1)} {
        align-self: nth($i, 2);
      }
    }

    // justify-*
    @each $i in $justify {
      .justify-#{nth($i, 1)} {
        justify-content: nth($i, 2);
      }
    }
  }

  @media screen and (min-width: $breakpoint-sm) {
    @each $i in $align {
      // align-sm-*
      .align-sm-#{nth($i, 1)} {
        align-items: nth($i, 2);
      }
      // align-content-sm-*
      .align-content-sm-#{nth($i, 1)} {
        align-content: nth($i, 2);
      }
      // align-self-sm-*
      .align-self-sm-#{nth($i, 1)} {
        align-self: nth($i, 2);
      }
    }

    // justify-sm-*
    @each $i in $justify {
      .justify-sm-#{nth($i, 1)} {
        justify-content: nth($i, 2);
      }
    }
  }

  @media screen and (min-width: $breakpoint-md) {
    @each $i in $align {
      .align-md-#{nth($i, 1)} {
        align-items: nth($i, 2);
      }
      .align-content-md-#{nth($i, 1)} {
        align-content: nth($i, 2);
      }
      .align-self-md-#{nth($i, 1)} {
        align-self: nth($i, 2);
      }
    }

    @each $i in $justify {
      .justify-md-#{nth($i, 1)} {
        justify-content: nth($i, 2);
      }
    }
  }

  @media screen and (min-width: $breakpoint-lg) {
    @each $i in $align {
      .align-lg-#{nth($i, 1)} {
        align-items: nth($i, 2);
      }
      .align-content-lg-#{nth($i, 1)} {
        align-content: nth($i, 2);
      }
      .align-self-lg-#{nth($i, 1)} {
        align-self: nth($i, 2);
      }
    }

    @each $i in $justify {
      .justify-lg-#{nth($i, 1)} {
        justify-content: nth($i, 2);
      }
    }
  }

  @media screen and (min-width: $breakpoint-xl) {
    @each $i in $align {
      .align-xl-#{nth($i, 1)} {
        align-items: nth($i, 2);
      }
      .align-content-xl-#{nth($i, 1)} {
        align-content: nth($i, 2);
      }
      .align-self-xl-#{nth($i, 1)} {
        align-self: nth($i, 2);
      }
    }

    @each $i in $justify {
      .justify-xl-#{nth($i, 1)} {
        justify-content: nth($i, 2);
      }
    }
  }

  @media screen and (min-width: $breakpoint-xxl) {
    @each $i in $align {
      .align-xxl-#{nth($i, 1)} {
        align-items: nth($i, 2);
      }
      .align-content-xxl-#{nth($i, 1)} {
        align-content: nth($i, 2);
      }
      .align-self-xxl-#{nth($i, 1)} {
        align-self: nth($i, 2);
      }
    }

    @each $i in $justify {
      .justify-xxl-#{nth($i, 1)} {
        justify-content: nth($i, 2);
      }
    }
  }

  .no-gutters {
    margin-right: 0;
    margin-left: 0;
    & > .bim-col, & > [class*=bim-col-] {
      padding: 0;
    }
  }
</style>
